import type {ComponentProps, ComponentPropsWithoutRef, ElementType} from 'react';
import {useRef} from 'react';
import classnames from 'classnames';
import {useTooltipTriggerState} from '@react-stately/tooltip';
import {useTooltipTrigger} from '@react-aria/tooltip';
import {mergeProps} from '@react-aria/utils';

import {Tooltip} from '../../../atoms/tooltip';

import styles from './file-preview-action.module.css';

type Props<Component extends ElementType = 'button'> = ComponentPropsWithoutRef<Component> & {
    as?: Component;
    tooltip?: ComponentProps<typeof Tooltip>['children'];
};

export const FilePreviewAction = <Component extends ElementType>({
    className,
    children,
    tooltip,
    as,
    ...props
}: Props<Component>) => {
    const ComponentProp: ElementType = as ?? 'button';
    const targetRef = useRef(null);
    const state = useTooltipTriggerState({delay: 400});

    const {triggerProps, tooltipProps} = useTooltipTrigger({}, state, targetRef);

    const combinedProps = Object.assign({}, mergeProps(triggerProps, props));

    return (
        <>
            <ComponentProp {...combinedProps} ref={targetRef} className={classnames(className, styles.button)}>
                <span className={styles.icon}>{children}</span>
            </ComponentProp>
            {tooltip && state.isOpen && (
                <Tooltip {...tooltipProps} state={state} targetRef={targetRef}>
                    {tooltip}
                </Tooltip>
            )}
        </>
    );
};
